import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarPriceDependents } from "./solar-price-dependents"
import type {
  SolarProposalExtendedWarrantiesDto,
  SolarProposalExtendedWarrantySelectionDto,
  UpdateSolarProposalExtendedWarrantyDto,
} from "../dto/solar-proposal-pricing-steps.dto"
import { solarProposalService } from "../services/solar-proposal.service"

/**
 * Chooses or removes (null) the Protection Plan. It is a pricing input, and
 * the header's Powur Care flag follows it, so both refetch.
 */
export default function useUpdateSolarProposalExtendedWarranty(): UseMutationResult<
  SolarProposalExtendedWarrantySelectionDto,
  unknown,
  { proposalId: string; body: UpdateSolarProposalExtendedWarrantyDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      solarProposalService.putExtendedWarranty(proposalId, body),
    onSuccess: ({ selected }, { proposalId }) => {
      const key = queryKeys.solarProposal.extendedWarranties(proposalId)
      queryClient.setQueryData<SolarProposalExtendedWarrantiesDto>(
        key,
        (current) => (current ? { ...current, selected } : current),
      )
      void queryClient.invalidateQueries({ queryKey: key, exact: true })
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`savings`],
      })
    },
  })
}
